import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLanguage } from "../../context/LanguageContext";
import { getSubscriptions } from "../../services/auth";
import { splitPlanLimits } from "../../utils/planLimits";
import Flag from "../../components/common/flag/Flag";
import "./LandingPage.css";

const GredusMark = ({ size = 40 }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 200 200" fill="none">
        <path d="M158 60a70 70 0 1 0 0 80" stroke="#3B82F6" stroke-width="22" stroke-linecap="round"></path>
        <path d="M86 102l18 18 38-44" stroke="#3B82F6" stroke-width="22" stroke-linecap="round" stroke-linejoin="round"></path>
    </svg>
);

const scrollTo = (id) => (e) => {
  e.preventDefault();
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const LanguageSwitcher = ({ block = false }) => {
  const { language, setLanguage, supportedLanguages } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const current =
    supportedLanguages.find((l) => l.code === language) || supportedLanguages[0];

  return (
    <div className={`lp-lang${block ? " lp-lang-block" : ""}`} ref={ref}>
      <button
        type="button"
        className="lp-lang-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="lp-lang-flag" aria-hidden="true">{current && <Flag code={current.code} size={15} />}</span>
        <span className="lp-lang-code">{current?.code?.toUpperCase()}</span>
        <svg
          className={`lp-lang-caret${open ? " open" : ""}`}
          width="12" height="12" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {open && (
        <ul className="lp-lang-menu" role="listbox">
          {supportedLanguages.map((lng) => (
            <li key={lng.code}>
              <button
                type="button"
                role="option"
                aria-selected={lng.code === language}
                className={`lp-lang-item${lng.code === language ? " active" : ""}`}
                onClick={() => {
                  setLanguage(lng.code);
                  setOpen(false);
                }}
              >
                <span className="lp-lang-flag" aria-hidden="true"><Flag code={lng.code} size={15} /></span>
                <span className="lp-lang-name">{lng.nativeName}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const Navbar = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <header className="lp-navbar-wrap">
      <div className="lp-navbar">
        <a href="#top" className="lp-brand" onClick={scrollTo("top")}>
          <GredusMark size={32} />
          <span className="lp-brandName">Gredus</span>
        </a>

        <nav className="lp-nav-links">
          <a href="#features" className="lp-nav-link" onClick={scrollTo("features")}>
            {t("pages.landing.navFeatures")}
          </a>
          <a href="#how" className="lp-nav-link" onClick={scrollTo("how")}>
            {t("pages.landing.navHow")}
          </a>
          <a href="#pricing" className="lp-nav-link" onClick={scrollTo("pricing")}>
            {t("pages.landing.navPricing")}
          </a>
          <a href="#faq" className="lp-nav-link" onClick={scrollTo("faq")}>
            {t("pages.landing.navFaq")}
          </a>
        </nav>

        <div className="lp-nav-actions">
          <LanguageSwitcher />
          <Link to="/login" className="lp-btn lp-btn-ghost">
            {t("pages.landing.navLogin")}
          </Link>
          <Link to="/register" className="lp-btn lp-btn-primary">
            {t("pages.landing.navStartFree")}
          </Link>
        </div>

        <button
          aria-label={t("pages.landing.menu")}
          className="lp-nav-toggle"
          onClick={() => setOpen((v) => !v)}
        >
          <span className={open ? "open" : ""}></span>
          <span className={open ? "open" : ""}></span>
          <span className={open ? "open" : ""}></span>
        </button>
      </div>

      {open && (
        <div className="lp-mobile-menu">
          <a href="#features" className="lp-nav-link" onClick={(e) => { scrollTo("features")(e); setOpen(false); }}>
            {t("pages.landing.navFeatures")}
          </a>
          <a href="#how" className="lp-nav-link" onClick={(e) => { scrollTo("how")(e); setOpen(false); }}>
            {t("pages.landing.navHow")}
          </a>
          <a href="#pricing" className="lp-nav-link" onClick={(e) => { scrollTo("pricing")(e); setOpen(false); }}>
            {t("pages.landing.navPricing")}
          </a>
          <a href="#faq" className="lp-nav-link" onClick={(e) => { scrollTo("faq")(e); setOpen(false); }}>
            {t("pages.landing.navFaq")}
          </a>
          <div className="lp-mobile-lang">
            <LanguageSwitcher block />
          </div>
          <div className="lp-mobile-actions">
            <Link to="/login" className="lp-btn lp-btn-ghost">{t("pages.landing.navLogin")}</Link>
            <Link to="/register" className="lp-btn lp-btn-primary">{t("pages.landing.navStartFree")}</Link>
          </div>
        </div>
      )}
    </header>
  );
};

const Hero = () => {
  const { t } = useTranslation();
  return (
    <section id="top" className="lp-hero">
      <div className="lp-hero-bg" aria-hidden="true">
        <div className="lp-hero-grid" />
        <div className="lp-hero-accent" />
      </div>

      <div className="lp-container lp-hero-inner">
        <div className="lp-hero-copy">
          <span className="lp-eyebrow">{t("pages.landing.heroEyebrow")}</span>

          <h1 className="lp-hero-title">
            {t("pages.landing.heroTitleStart")} <span className="lp-accent">{t("pages.landing.heroTitleAccent")}</span>
            {t("pages.landing.heroTitleEnd")}
          </h1>

          <p className="lp-hero-sub">
            {t("pages.landing.heroSub")}
          </p>

          <div className="lp-hero-cta">
            <Link to="/register" className="lp-btn lp-btn-primary lp-btn-lg">
              {t("pages.landing.heroCtaPrimary")}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M5 12h14M13 5l7 7-7 7"/>
              </svg>
            </Link>
            <a href="#how" onClick={scrollTo("how")} className="lp-hero-play">
              <span className="lp-hero-play-icon">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M8 5v14l11-7z"/>
                </svg>
              </span>
              {t("pages.landing.heroCtaSecondary")}
            </a>
          </div>

          {/*<p className="lp-hero-trust">*/}
          {/*  {t("pages.landing.heroTrust")}*/}
          {/*</p>*/}
        </div>

        <div className="lp-hero-visual" aria-hidden="true">
          <div className="lp-exam-card">
            <div className="lp-exam-head">
              <div className="lp-exam-dots">
                <i className="on" /><i className="on" /><i className="on" /><i /><i /><i /><i /><i /><i /><i />
              </div>
              <span className="lp-exam-timer">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" />
                </svg>
                12:45
              </span>
            </div>

            <div className="lp-exam-q">2x + 5 = 15,&nbsp;&nbsp;x = ?</div>

            <ul className="lp-exam-options">
              <li><span className="lp-exam-letter">A</span> 3</li>
              <li className="sel">
                <span className="lp-exam-letter">B</span> 5
                <svg className="lp-exam-check" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 6L9 17l-5-5" />
                </svg>
              </li>
              <li><span className="lp-exam-letter">C</span> 7</li>
              <li><span className="lp-exam-letter">D</span> 10</li>
            </ul>
          </div>

          <div className="lp-exam-badge">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 6L9 17l-5-5" />
            </svg>
            92%
          </div>
        </div>
      </div>
    </section>
  );
};

const useFeatures = () => {
  const { t } = useTranslation();
  return [
    {
      title: t("pages.landing.feat1Title"),
      desc: t("pages.landing.feat1Desc"),
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <ellipse cx="12" cy="5" rx="9" ry="3" />
          <path d="M21 5v14c0 1.66-4.03 3-9 3s-9-1.34-9-3V5" />
          <path d="M21 12c0 1.66-4.03 3-9 3s-9-1.34-9-3" />
        </svg>
      ),
    },
    {
      title: t("pages.landing.feat2Title"),
      desc: t("pages.landing.feat2Desc"),
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 3l1.8 4.7L18.5 9l-4.7 1.8L12 15.5l-1.8-4.7L5.5 9l4.7-1.3z" />
          <path d="M19 14.5l.7 1.9 1.8.6-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.6z" />
        </svg>
      ),
    },
    {
      title: t("pages.landing.feat3Title"),
      desc: t("pages.landing.feat3Desc"),
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <path d="M14 2v6h6" />
          <path d="M9 13h6M9 17h6M9 9h1" />
        </svg>
      ),
    },
    {
      title: t("pages.landing.feat4Title"),
      desc: t("pages.landing.feat4Desc"),
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 22s8-3.5 8-10V5l-8-3-8 3v7c0 6.5 8 10 8 10z" />
          <path d="M9 12l2 2 4-4" />
        </svg>
      ),
    },
    {
      title: t("pages.landing.feat5Title"),
      desc: t("pages.landing.feat5Desc"),
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 3v18h18" />
          <rect x="7" y="11" width="3" height="6" rx="0.5" />
          <rect x="12" y="7" width="3" height="10" rx="0.5" />
          <rect x="17" y="14" width="3" height="3" rx="0.5" />
        </svg>
      ),
    },
    {
      title: t("pages.landing.feat6Title"),
      desc: t("pages.landing.feat6Desc"),
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      ),
    },
  ];
};

const Features = () => {
  const { t } = useTranslation();
  const features = useFeatures();
  return (
    <section id="features" className="lp-section">
      <div className="lp-container">
        <div className="lp-section-head">
          <span className="lp-eyebrow">{t("pages.landing.featuresEyebrow")}</span>
          <h2 className="lp-section-title">
            {t("pages.landing.featuresTitleStart")} <span className="lp-accent">{t("pages.landing.featuresTitleAccent")}</span>
          </h2>
          <p className="lp-section-sub">
            {t("pages.landing.featuresSub")}
          </p>
        </div>

        <div className="lp-features">
          {features.map((f, i) => (
            <article key={f.title} className="lp-feature">
              <div className="lp-feature-top">
                <span className="lp-feature-num">{String(i + 1).padStart(2, "0")}</span>
                <span className="lp-feature-icon">{f.icon}</span>
              </div>
              <h3 className="lp-feature-title">{f.title}</h3>
              <p className="lp-feature-desc">{f.desc}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

const useSteps = () => {
  const { t } = useTranslation();
  return [
    {
      n: "01",
      title: t("pages.landing.step1Title"),
      desc: t("pages.landing.step1Desc"),
    },
    {
      n: "02",
      title: t("pages.landing.step2Title"),
      desc: t("pages.landing.step2Desc"),
    },
    {
      n: "03",
      title: t("pages.landing.step3Title"),
      desc: t("pages.landing.step3Desc"),
    },
  ];
};

const HowItWorks = () => {
  const { t } = useTranslation();
  const steps = useSteps();
  return (
    <section id="how" className="lp-section lp-section-alt">
      <div className="lp-container">
        <div className="lp-section-head">
          <span className="lp-eyebrow">{t("pages.landing.howEyebrow")}</span>
            <h2 className="lp-section-title">
                {t("pages.landing.howTitle")} <span className="lp-accent">{t("pages.landing.howTitleAccent")}</span>
            </h2>
          <p className="lp-section-sub">
            {t("pages.landing.howSub")}
          </p>
        </div>

        <div className="lp-steps">
          {steps.map((s, i) => (
            <div key={s.n} className="lp-step">
              <div className="lp-step-num">{s.n}</div>
              <h3 className="lp-step-title">{s.title}</h3>
              <p className="lp-step-desc">{s.desc}</p>
              {i < steps.length - 1 && <div className="lp-step-arrow" aria-hidden="true">→</div>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const useFaqs = () => {
  const { t } = useTranslation();
  return [
    { q: t("pages.landing.faq1Q"), a: t("pages.landing.faq1A") },
    { q: t("pages.landing.faq2Q"), a: t("pages.landing.faq2A") },
    { q: t("pages.landing.faq3Q"), a: t("pages.landing.faq3A") },
    { q: t("pages.landing.faq4Q"), a: t("pages.landing.faq4A") },
    { q: t("pages.landing.faq5Q"), a: t("pages.landing.faq5A") },
    { q: t("pages.landing.faq6Q"), a: t("pages.landing.faq6A") },
  ];
};

const FAQ = () => {
  const { t } = useTranslation();
  const faqs = useFaqs();
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="lp-section lp-section-alt">
      <div className="lp-container lp-container-narrow">
        <div className="lp-section-head">
          <span className="lp-eyebrow">{t("pages.landing.faqEyebrow")}</span>
          <h2 className="lp-section-title">{t("pages.landing.faqTitle")}</h2>
        </div>

        <div className="lp-faq">
          {faqs.map((item, i) => {
            const isOpen = open === i;
            return (
              <div key={item.q} className={`lp-faq-item ${isOpen ? "lp-faq-open" : ""}`}>
                <button
                  className="lp-faq-q"
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  aria-expanded={isOpen}
                >
                  <span>{item.q}</span>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="lp-faq-icon">
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>
                {isOpen && <div className="lp-faq-a">{item.a}</div>}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const Pricing = () => {
  const { t } = useTranslation();
  const [plans, setPlans] = useState([]);
  // Cards whose full limit table is expanded, by plan id.
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  // The yearly plan is the default, recommended offer; fall back to monthly if the
  // catalogue has no yearly rows (e.g. frontend deployed ahead of the migration).
  const [billing, setBilling] = useState("YEARLY");

  useEffect(() => {
    let active = true;
    // Public endpoint (/website/subscriptions) — no auth needed. axios already
    // sends Accept-Language, so plan names + limit labels come back localized.
    getSubscriptions()
      .then((data) => {
        if (!active) return;
        const list = Array.isArray(data) ? data : [];
        setPlans(list);
        if (!list.some((p) => p.billingType === "YEARLY")) setBilling("MONTHLY");
      })
      .catch(() => { if (active) setPlans([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const shown = plans.filter((p) => p.billingType === billing);
  const billingTabs = ["YEARLY", "MONTHLY", "ONE_TIME"]
    .filter((type) => plans.some((p) => p.billingType === type));

  return (
    <section id="pricing" className="lp-section">
      <div className="lp-container">
        <div className="lp-section-head">
          <span className="lp-eyebrow">{t("pages.landing.pricingEyebrow")}</span>
          <h2 className="lp-section-title">
            {t("pages.landing.pricingTitleStart")} <span className="lp-accent">{t("pages.landing.pricingTitleAccent")}</span>
          </h2>
          <p className="lp-section-sub">{t("pages.landing.pricingSub")}</p>
        </div>

        {billingTabs.length > 1 && (
          <div className="lp-pricing-toggle" role="tablist">
            {billingTabs.map((type) => (
              <button
                key={type}
                type="button"
                role="tab"
                aria-selected={billing === type}
                className={`lp-pricing-toggleBtn ${billing === type ? "active" : ""}`}
                onClick={() => setBilling(type)}
              >
                {type === "YEARLY"
                  ? t("pages.landing.pricingYearly", { defaultValue: "İllik" })
                  : type === "MONTHLY"
                    ? t("pages.landing.pricingMonthly")
                    : t("pages.landing.pricingOneTime")}
                {type === "YEARLY" && (
                  <span
                    style={{
                      marginLeft: 6, padding: "1px 7px", borderRadius: 999,
                      background: "#2563eb", color: "#fff", fontSize: 10, fontWeight: 700,
                      verticalAlign: "middle",
                    }}
                  >
                    {t("pages.landing.pricingRecommended", { defaultValue: "Tövsiyə olunur" })}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
        {billing === "YEARLY" && shown.length > 0 && (
          <p className="lp-pricing-note">
            {t("pages.landing.pricingYearlyNote", { defaultValue: "12 ay — 10 ayın qiymətinə · 2 ay pulsuz" })}
          </p>
        )}
        {billing === "ONE_TIME" && shown.length > 0 && (
          <p className="lp-pricing-note">
            {t("pages.landing.pricingOneTimeNote", { defaultValue: "Bir imtahan kampaniyası üçün — vaxtı bitmir" })}
          </p>
        )}

        {loading ? (
          <p className="lp-pricing-loading">{t("pages.landing.pricingLoading")}</p>
        ) : (
          <div className="lp-pricing-grid">
            {shown.map((plan) => {
              const popular = (plan.code || "").includes("STANDARD");
              // Students + exams lead the card — a director reads those two numbers and knows
              // which tier is theirs. The rest lives behind "Bütün limitlər".
              const { primary, rest, collapsible } = splitPlanLimits(plan.limits);
              const isOpen = expandedIds.has(plan.id);
              const visibleLimits = collapsible ? (isOpen ? [...primary, ...rest] : primary) : (plan.limits || []);
              return (
                <article key={plan.id} className={`lp-pricing-card ${popular ? "popular" : ""}`}>
                  {popular && <span className="lp-pricing-badge">{t("pages.landing.pricingPopular")}</span>}
                  <h3 className="lp-pricing-name">{plan.name}</h3>
                  <div className="lp-pricing-price">
                    <span className="lp-pricing-amount">{Number(plan.price)}</span>
                    <span className="lp-pricing-currency">{t("pages.landing.pricingCurrency")}</span>
                    <span className="lp-pricing-period">
                      {billing === "YEARLY"
                        ? t("pages.landing.pricingPerYear", { defaultValue: "/il" })
                        : billing === "MONTHLY"
                          ? t("pages.landing.pricingPerMonth")
                          : t("pages.landing.pricingPerOneTime")}
                    </span>
                  </div>
                  <ul className="lp-pricing-limits">
                    {visibleLimits.map((l, i) => (
                      <li key={i} className="lp-pricing-limit">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M20 6L9 17l-5-5" />
                        </svg>
                        {/* Video quota is stored in SECONDS (V23); show marketing minutes. */}
                        <span><strong>{l.limitValue === -1 ? "∞"
                          : l.limitCode === "VIDEO_MINUTES_LIMIT" ? Math.round(l.limitValue / 60)
                          : l.limitValue}</strong> {l.limitName}</span>
                      </li>
                    ))}
                  </ul>
                  {collapsible && (
                    <button
                      type="button"
                      className="lp-pricing-more"
                      aria-expanded={isOpen}
                      onClick={() =>
                        setExpandedIds((prev) => {
                          const next = new Set(prev);
                          if (next.has(plan.id)) next.delete(plan.id); else next.add(plan.id);
                          return next;
                        })
                      }
                    >
                      {t("pages.landing.pricingAllLimits", { defaultValue: "Bütün limitlər" })}
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </button>
                  )}
                  <Link to="/register" className={`lp-btn ${popular ? "lp-btn-primary" : "lp-btn-ghost"} lp-btn-block`}>
                    {t("pages.landing.pricingCta")}
                  </Link>
                </article>
              );
            })}
          </div>
        )}

        {/* Xüsusi — the tier without numbers. Schools and multi-hundred-student centers don't
            pick a card, they talk to us. One full-width band, below every plan group,
            independent of the active tab. */}
        <div className="lp-pricing-custom">
          <div className="lp-pricing-custom-text">
            <h3 className="lp-pricing-custom-title">{t("pages.landing.pricingCustomTitle", { defaultValue: "Xüsusi" })}</h3>
            <p className="lp-pricing-custom-sub">{t("pages.landing.pricingCustomText", { defaultValue: "Məktəb və ya böyük tədris mərkəzisiniz? Sizin üçün xüsusi paket hazırlayaq." })}</p>
          </div>
          <a
            className="lp-btn lp-btn-primary lp-pricing-custom-cta"
            href={`mailto:info@gredus.com?subject=${encodeURIComponent(t("pages.landing.pricingCustomMailSubject", { defaultValue: "Xüsusi paket sorğusu" }))}`}
          >
            {t("pages.landing.pricingCustomCta", { defaultValue: "Əlaqə saxlayın" })}
          </a>
        </div>
      </div>
    </section>
  );
};

const FinalCTA = () => {
  const { t } = useTranslation();
  return (
    <section className="lp-cta">
      <div className="lp-container">
        <div className="lp-cta-card">
          <div className="lp-cta-grid" aria-hidden="true">
            {Array.from({ length: 9 }).map((_, i) => {
              const accent = [2, 3, 7].includes(i);
              return (
                <span
                  key={i}
                  className={accent ? "lp-cta-cell lp-cta-cell-accent" : "lp-cta-cell"}
                />
              );
            })}
          </div>
          <div className="lp-cta-inner">
            <h2 className="lp-cta-title">{t("pages.landing.ctaTitle")}</h2>
            <p className="lp-cta-sub">
              {t("pages.landing.ctaSub")}
            </p>
            <div className="lp-cta-actions">
              <Link to="/register" className="lp-btn lp-btn-cobalt-on-ink lp-btn-lg">
                {t("pages.landing.ctaPrimary")}
              </Link>
              <Link to="/login" className="lp-btn lp-btn-ink-ghost lp-btn-lg">
                {t("pages.landing.ctaSecondary")}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Footer = () => {
  const { t } = useTranslation();
  return (
    <footer className="lp-footer">
      <div className="lp-container">
        <div className="lp-footer-top">
          <div className="lp-footer-brand">
            <div className="lp-brand">
              <GredusMark size={28} />
              <span className="lp-brandName">Gredus</span>
            </div>
            <p className="lp-footer-tag">
              {t("pages.landing.footerTag")}
            </p>
          </div>

          <div className="lp-footer-cols">
            <div className="lp-footer-col">
              <h4>{t("pages.landing.footerProduct")}</h4>
              <a href="#features" onClick={scrollTo("features")}>{t("pages.landing.navFeatures")}</a>
              <a href="#how" onClick={scrollTo("how")}>{t("pages.landing.navHow")}</a>
              <a href="#pricing" onClick={scrollTo("pricing")}>{t("pages.landing.navPricing")}</a>
              <a href="#faq" onClick={scrollTo("faq")}>{t("pages.landing.navFaq")}</a>
              <Link to="/terms" target="_blank" rel="noopener noreferrer">{t("legal.agreementTitle")}</Link>
            </div>
            <div className="lp-footer-col">
              <h4>{t("pages.landing.footerAccount")}</h4>
              <Link to="/login">{t("pages.landing.footerAccountLogin")}</Link>
              <Link to="/register">{t("pages.landing.footerAccountRegister")}</Link>
            </div>
            <div className="lp-footer-col">
              <h4>{t("pages.landing.footerContact")}</h4>
              <a href="mailto:info@gredus.com">info@gredus.com</a>
              <a href="tel:+994502011280">050 201 12 80</a>
            </div>
          </div>
        </div>

        <div className="lp-footer-bottom">
          <span>© {new Date().getFullYear()} Gridsoft MMC. {t("pages.landing.footerCopyright")}</span>
          <div className="lp-footer-social">
            <a href="https://instagram.com/gredusedu" target="_blank" rel="noreferrer" aria-label="Instagram">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="2" width="20" height="20" rx="5" />
                <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
              </svg>
            </a>
            <a href="https://facebook.com" target="_blank" rel="noreferrer" aria-label="Facebook">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
              </svg>
            </a>
            <a href="https://linkedin.com" target="_blank" rel="noreferrer" aria-label="LinkedIn">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-4 0v7h-4v-7a6 6 0 0 1 6-6z" />
                <rect x="2" y="9" width="4" height="12" />
                <circle cx="4" cy="4" r="2" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

const LandingPage = () => (
  <div className="lp-page">
    <Navbar />
    <main>
      <Hero />
      <Features />
      <HowItWorks />
      <Pricing />
      <FAQ />
      <FinalCTA />
    </main>
    <Footer />
  </div>
);

export default LandingPage;
